/**
 * TermRepaymentSection — Computed term & repayment display (read-only)
 *
 * Sprint: SPRINT-30
 * Feature: F017 — Section 4 (Term & Repayment)
 *
 * Derives values from the pricing schedule regimes:
 * - Total term: Σ(regime.regimeMonths)
 * - IO months: Σ months from IO regimes
 * - Repayment type: computed label (IO, P&I, IO then P&I)
 */

import { useMemo } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import type { PricingScheduleFormData } from "./pricing-schedule-section";

export interface TermRepaymentSectionProps {
  schedules: PricingScheduleFormData[];
}

export function TermRepaymentSection({ schedules }: TermRepaymentSectionProps) {
  const computed = useMemo(() => {
    let totalTerm = 0;
    let ioMonths = 0;
    let hasIO = false;
    let hasPI = false;

    for (const s of schedules) {
      const months = s.regimeMonths ?? 0;
      totalTerm += months;
      if (s.paymentType === "interest_only") {
        ioMonths += months;
        hasIO = true;
      } else {
        hasPI = true;
      }
    }

    let repaymentType = "-";
    if (hasIO && hasPI) repaymentType = "IO then P&I";
    else if (hasIO) repaymentType = "IO";
    else if (hasPI) repaymentType = "P&I";

    return {
      totalTerm: totalTerm || null,
      ioMonths: ioMonths || null,
      repaymentType,
    };
  }, [schedules]);

  return (
    <div data-testid="term-repayment-section" className="space-y-3">
      <Label className="text-sm font-medium">Term & Repayment (computed)</Label>
      <div className="grid grid-cols-3 gap-3 border rounded-lg p-4 bg-muted/20">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Total Term (months)</Label>
          <Input
            data-testid="computed-total-term"
            value={computed.totalTerm ?? "-"}
            readOnly
            disabled
            className="bg-muted h-9"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">IO Months</Label>
          <Input
            data-testid="computed-io-months"
            value={computed.ioMonths ?? "-"}
            readOnly
            disabled
            className="bg-muted h-9"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Repayment Type</Label>
          <Input
            data-testid="computed-repayment-type"
            value={computed.repaymentType}
            readOnly
            disabled
            className="bg-muted h-9"
          />
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Derived from pricing schedule regimes above. Repayment amounts calculated after save.
      </p>
    </div>
  );
}

export default TermRepaymentSection;
